import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomModal from "../../common/CustomModal";
import AgencyAPIs from "../../../apiRoutes/Agency";
import { GET_AGENCIES_QUERY_KEY } from "../../../utils/constants/queryKeys";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import toast from "react-hot-toast";

const HOOK_SUMMARY_QUERY_KEY = ["rateCardHookSummary"];

const HookPricingModal = ({ open, onClose, agency }) => {
  const [costPerHook, setCostPerHook] = useState(agency?.cost_per_hook ?? "");
  const [maxFreeHooks, setMaxFreeHooks] = useState(agency?.max_free_hooks ?? "");
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open && agency) {
      setCostPerHook(agency.cost_per_hook ?? "");
      setMaxFreeHooks(agency.max_free_hooks ?? "");
    }
  }, [open, agency]);

  const { mutate: submit, isPending } = useMutation({
    mutationFn: (payload) => AgencyAPIs.update({ axiosPrivate, id: agency.id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AGENCIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: HOOK_SUMMARY_QUERY_KEY });
      toast.success("Hook pricing updated.");
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.error_message || e?.message),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      cost_per_hook: costPerHook !== "" ? String(costPerHook).trim() : null,
      max_free_hooks: maxFreeHooks !== "" ? parseInt(maxFreeHooks, 10) : null,
    };
    submit(payload);
  };

  return (
    <CustomModal
      open={open}
      title={`Hook Pricing - ${agency?.name ?? ""}`}
      onClose={onClose}
      handleConfirmation={handleSubmit}
      handleConfirmationBtnText="Save"
      isLoading={isPending}
    >
      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <p className="text-xs text-muted-foreground">
          Billing kicks in only when hooks used &gt; max free hooks. Total cost = max(0, hooks used − max free hooks) × cost per hook.
        </p>
        <div className="space-y-2">
          <Label>Max free hooks</Label>
          <Input
            type="number"
            min="0"
            placeholder="e.g. 10"
            value={maxFreeHooks}
            onChange={(e) => setMaxFreeHooks(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Number of hooks before billing starts. Leave blank for unlimited free.</p>
        </div>
        <div className="space-y-2">
          <Label>Cost per hook</Label>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="e.g. 500.00"
            value={costPerHook}
            onChange={(e) => setCostPerHook(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Amount charged per hook beyond the free limit.</p>
        </div>
      </form>
    </CustomModal>
  );
};

export default HookPricingModal;
